import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, LogIn, UserPlus } from 'lucide-react';
import { signUpWithEmail, signInWithEmail, signInWithGoogle } from '../../../shared/services/authService';
import type { AuthError } from '../../../shared/services/authService';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialMode?: 'login' | 'signup';
}

export function AuthModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  initialMode = 'login' 
}: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      let result;
      if (mode === 'signup') {
        result = await signUpWithEmail(email, password);
      } else {
        result = await signInWithEmail(email, password);
      }

      if (result.error) {
        setError(result.error.message);
        setIsLoading(false);
      } else if (result.user) {
        setIsLoading(false);
        onSuccess?.();
        onClose();
        // Reset form
        setEmail('');
        setPassword('');
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || '操作失败，请重试');
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await signInWithGoogle();
      if (result.error) {
        setError(result.error.message);
        setIsLoading(false);
      }
      // If successful, user will be redirected to OAuth callback
      // The modal will be closed when they return
    } catch (err: any) {
      setError(err.message || 'Google登录失败，请重试');
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 2.0.0: 双主题适配 - Modal 容器：亮色默认 + 暗色变体 */}
            <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/50 w-full max-w-md">
              {/* Header */}
              {/* 2.0.0: 双主题适配 - Header border：亮色默认 + 暗色变体 */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                {/* 2.0.0: 双主题适配 - Header 标题：亮色默认 + 暗色变体 */}
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {mode === 'login' ? '登录' : '注册'}
                </h2>
                {/* 2.0.0: 双主题适配 - 关闭按钮：亮色默认 + 暗色变体 */}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Error Message */}
                {/* 2.0.0: 双主题适配 — 错误消息：亮色默认 + 暗色变体 */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Email/Password Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email Field */}
                  <div>
                    {/* 2.0.0: 双主题适配 - Label：亮色默认 + 暗色变体 */}
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      邮箱
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {/* 2.0.0: 双主题适配 — 输入框：亮色默认 + 暗色变体 */}
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    {/* 2.0.0: 双主题适配 - Label：亮色默认 + 暗色变体 */}
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      密码
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {/* 2.0.0: 双主题适配 — 输入框：亮色默认 + 暗色变体 */}
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        minLength={6}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="至少6个字符"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : mode === 'login' ? (
                      <>
                        <LogIn className="w-5 h-5" />
                        登录
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        注册
                      </>
                    )}
                  </button>
                </form>

                {/* Divider */}
                {/* 2.0.0: 双主题适配 - 分隔线：亮色默认 + 暗色变体 */}
                <div className="my-6 flex items-center">
                  <div className="flex-1 border-t border-gray-200 dark:border-gray-800"></div>
                  <span className="px-4 text-sm text-gray-500">或</span>
                  <div className="flex-1 border-t border-gray-200 dark:border-gray-800"></div>
                </div>

                {/* Google Sign In Button */}
                {/* 2.0.0: 双主题适配 - Google 按钮：亮色默认 + 暗色变体 */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full py-2.5 bg-gray-50 dark:bg-white hover:bg-gray-100 dark:hover:bg-gray-100 text-gray-900 font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  使用 Google 登录
                </button>

                {/* Switch Mode */}
                {/* 2.0.0: 双主题适配 - 切换模式文字：亮色默认 + 暗色变体 */}
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  {mode === 'login' ? (
                    <>
                      还没有账户？{' '}
                      <button
                        onClick={switchMode}
                        className="text-orange-500 hover:text-orange-400 font-medium"
                      >
                        立即注册
                      </button>
                    </>
                  ) : (
                    <>
                      已有账户？{' '}
                      <button
                        onClick={switchMode}
                        className="text-orange-500 hover:text-orange-400 font-medium"
                      >
                        立即登录
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

